import { ChangeDetectionStrategy, Component, computed, inject, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { HmiLookupService } from '../../../services/hmi-lookup.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'departman-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzSelectModule,
    CdkTextareaAutosize],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dep-edit">
      <div class="dep-baslik">
        <div class="dep-baslik-ikon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        </div>
        <span>Departman Bilgileri</span>
      </div>

      <div class="dep-form">
        <div class="form-grup">
          <label>Departman Adı <span class="zorunlu">*</span></label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.departmanAdi || ''"
              (ngModelChange)="guncelle('departmanAdi', $event)"
              placeholder="Departman adını girin..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Departman Kodu</label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.departmanKodu || ''"
              (ngModelChange)="guncelle('departmanKodu', $event)"
              placeholder="Departman kodu"/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="dep-ayirici"></div>

        <div class="form-grup">
          <label>Üst Departman</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.ustDepartman"
              (ngModelChange)="guncelle('ustDepartman', $event)"
              nzPlaceHolder="Üst departman seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="ustDeptTpl">
              @for (d of lookup.departmanlar(); track d._id) {
                <nz-option [nzValue]="d.ad" [nzLabel]="d.ad" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">🏢</span><div class="sel-bilgi"><span class="sel-ad">{{ d.ad }}</span>@if (d.kod) { <span class="sel-detay">{{ d.kod }}</span> }</div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #ustDeptTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">🏢</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Departman Yöneticisi</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.yonetici"
              (ngModelChange)="guncelle('yonetici', $event)"
              nzPlaceHolder="Yönetici seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="yoneticiTpl">
              @for (per of lookup.personeller(); track per._id) {
                <nz-option [nzValue]="per.ad + ' ' + per.soyad" [nzLabel]="per.ad + ' ' + per.soyad" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">👤</span><div class="sel-bilgi"><span class="sel-ad">{{ per.ad }} {{ per.soyad }}</span><span class="sel-detay">{{ per.unvan || per.gorev || '' }}{{ per.sicilNo ? ' · ' + per.sicilNo : '' }}</span></div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #yoneticiTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">👤</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="dep-ayirici"></div>

        <div class="form-grup">
          <label>Renk</label>
          <div class="renk-secici">
            <div class="renk-onizleme"
              [style.background]="p()?.renk || '#7c3aed'">
              <div class="renk-ic-halka"></div>
            </div>
            <div class="renk-bilgi">
              <span class="renk-hex">{{ p()?.renk || '#7c3aed' }}</span>
              <span class="renk-aciklama">Departman temsil rengi</span>
            </div>
            <input type="color"
              class="renk-input"
              [ngModel]="p()?.renk || '#7c3aed'"
              (ngModelChange)="guncelle('renk', $event)"/>
          </div>
        </div>

        <div class="dep-ayirici"></div>

        <div class="form-grup">
          <label>Açıklama</label>
          <div class="input-wrap">
            <textarea nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.aciklama || ''"
              (ngModelChange)="guncelle('aciklama', $event)"
              placeholder="Departman hakkında kısa bir açıklama..."
              cdkTextareaAutosize [cdkAutosizeMinRows]="2" [cdkAutosizeMaxRows]="4">
            </textarea>
            <div class="input-glow"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    :host { display: block; }

    .dep-edit {
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
    }

    .dep-baslik {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      margin-bottom: 4px;
      border-radius: 10px;
      background: linear-gradient(
        135deg,
        var(--py-arkaplan-soluk, rgba(0,0,0,0.03)),
        transparent
      );
    }

    .dep-baslik-ikon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #7c3aed, #a855f7);
      color: #fff;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(124, 58, 237, 0.35);
    }

    .dep-baslik span {
      font-size: 13px;
      font-weight: 600;
      color: var(--py-metin, #333);
      letter-spacing: 0.3px;
    }

    .dep-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 4px 2px;
    }

    .dep-ayirici {
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent,
        var(--py-kenarlik, #d9d9d9) 20%,
        var(--py-kenarlik, #d9d9d9) 80%,
        transparent
      );
      opacity: 0.4;
      margin: 2px 0;
    }

    .form-grup {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .form-grup label {
      font-size: 11px;
      font-weight: 500;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
      text-transform: uppercase;
      letter-spacing: 0.6px;
      padding-left: 2px;
      transition: color 0.2s ease;
    }

    .form-grup:focus-within label {
      color: var(--py-link, #1890ff);
    }

    .zorunlu {
      color: #ff4d4f;
      font-size: 13px;
      line-height: 1;
      vertical-align: middle;
    }

    .input-wrap {
      position: relative;
    }

    .input-glow {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      border-radius: 1px;
      background: linear-gradient(90deg, #7c3aed, #1890ff, #7c3aed);
      transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  left 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.3s ease;
      opacity: 0;
      pointer-events: none;
      z-index: 1;
    }

    .input-wrap:focus-within .input-glow {
      width: 100%;
      left: 0;
      opacity: 1;
    }

    .input-wrap input[nz-input],
    .input-wrap textarea[nz-input] {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px;
      padding: 8px 12px;
      font-size: 13px;
      color: var(--py-metin, #333);
      transition: border-color 0.25s ease,
                  background 0.25s ease,
                  box-shadow 0.25s ease;
    }

    .input-wrap input[nz-input]:hover,
    .input-wrap textarea[nz-input]:hover {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      background: var(--py-arkaplan-hover, #f5f5f5);
    }

    .input-wrap input[nz-input]:focus,
    .input-wrap textarea[nz-input]:focus {
      border-color: var(--py-kenarlik-aktif, #1890ff);
      background: var(--py-input-arka, #fff);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      outline: none;
    }

    .input-wrap ::ng-deep .ant-select-selector {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 10px !important;
      min-height: 44px;
      font-size: 15px;
      transition: border-color 0.25s ease,
                  background 0.25s ease,
                  box-shadow 0.25s ease;
    }

    .input-wrap:hover ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-hover, #b3b3b3) !important;
      background: var(--py-arkaplan-hover, #f5f5f5) !important;
    }

    .input-wrap:focus-within ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-aktif, #1890ff) !important;
      background: var(--py-input-arka, #fff) !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1) !important;
    }

    .renk-secici {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 10px;
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: border-color 0.25s ease,
                  box-shadow 0.25s ease,
                  transform 0.2s ease;
    }

    .renk-secici:hover {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      transform: translateY(-1px);
    }

    .renk-onizleme {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow:
        0 2px 8px rgba(0,0,0,0.15),
        inset 0 -2px 4px rgba(0,0,0,0.1),
        inset 0 2px 4px rgba(255,255,255,0.2);
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                  box-shadow 0.3s ease;
    }

    .renk-secici:hover .renk-onizleme {
      transform: scale(1.1);
      box-shadow:
        0 4px 14px rgba(0,0,0,0.2),
        inset 0 -2px 4px rgba(0,0,0,0.1),
        inset 0 2px 4px rgba(255,255,255,0.2);
    }

    .renk-ic-halka {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: rgba(255,255,255,0.35);
      backdrop-filter: blur(2px);
    }

    .renk-bilgi {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .renk-hex {
      font-size: 13px;
      font-weight: 600;
      color: var(--py-metin, #333);
      font-family: 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
      letter-spacing: 0.5px;
    }

    .renk-aciklama {
      font-size: 10px;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
    }

    .renk-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
      border: none;
    }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }

    .dep-baslik-ikon {
      animation: none;
      position: relative;
    }

    .dep-baslik-ikon::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,255,255,0.2) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: shimmer 3s infinite linear;
      pointer-events: none;
    }
  `],
})
export class DepartmanEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  lookup = inject(HmiLookupService);

  constructor() { super(); this.lookup.departmanlariYukle(); this.lookup.personelleriYukle(); }

  p = computed(() => this.parametreler()?.ek || {});

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (params?.ek) {
      params.ek[alan] = deger;
      this.parametreler.set({ ...params });
    }
  }
}
